import React, { useMemo, useState } from 'react'
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime, formatTenantDate } from '../../../utils/formatTenantDateTime'
import LevelBadge from '../shared/LevelBadge'

interface BusinessErrorRow {
  code: string
  description: string
  level: string
  scenarios_count: number
  last_detected_at: string | null
  counting_since: string | null
  total_occurrences: number
}

interface BusinessErrorsTableProps {
  business_errors: BusinessErrorRow[]
  localization_context?: LocalizationContextType
}

const BusinessErrorsTable = ({ business_errors, localization_context }: BusinessErrorsTableProps) => {
  const [query, setQuery] = useState('')

  // The catalog is a fixed, code-defined list of about sixty rows, so searching it is a filter over
  // what is already on the page - a server round-trip and pagination would be ceremony.
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return business_errors

    return business_errors.filter(({ code, description }) =>
      code.toLowerCase().includes(needle) || description.toLowerCase().includes(needle)
    )
  }, [business_errors, query])

  // Counting starts at deploy, with no backfill, so on day one every row reads 0 / —. Said once
  // here it is a launch state; left unsaid it reads as a broken page. Once anything has been
  // counted the per-row "since" dates carry the same fact more precisely, so this goes away.
  const nothingCountedYet = business_errors.every(({ total_occurrences }) => total_occurrences === 0)

  return (
    <>
      {nothingCountedYet && (
        <p className="text-muted small">
          Occurrence counts start from the day error codes were introduced; alerts recorded before
          then are not counted.
        </p>
      )}

      <div className="mb-3">
        <label className="visually-hidden" htmlFor="business-error-search">Search business errors</label>
        <input
          id="business-error-search"
          type="search"
          className="form-control"
          style={{ maxWidth: '360px' }}
          placeholder="Search by code or description"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-robis">
          <thead className="thead-robis">
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Description</th>
              <th scope="col">Level</th>
              <th scope="col">Scenarios</th>
              <th scope="col">Last Detected</th>
              <th scope="col">Total Occurrences</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.code}>
                <td><code className="fw-bold">{row.code}</code></td>
                <td>{row.description}</td>
                <td><LevelBadge level={row.level} /></td>
                <td>{row.scenarios_count}</td>
                <td className="small">
                  {row.last_detected_at ? formatTenantDateTime(row.last_detected_at, localization_context) : '—'}
                </td>
                <td>
                  {row.total_occurrences}
                  {/* Codes were tagged onto call sites across more than one release, so "0" means
                      different things for different rows without the date counting started. */}
                  {row.counting_since && (
                    <span className="text-muted small ms-2">
                      since {formatTenantDate(row.counting_since, localization_context)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className="text-muted">No business errors match “{query}”.</p>
      )}
    </>
  )
}

export default BusinessErrorsTable
